import React, { useState, useRef, useEffect, useMemo } from "react";
import { getRecap, postShareRecap, getSharedRecap } from "../../api/mainApi";
import { useTelegram } from "../../context/TelegramContext";
import { useTranslation } from "react-i18next";
import {
  FaChevronLeft,
  FaBrain,
  FaChartArea,
  FaTrophy,
  FaFire,
  FaGift,
  FaGifts,
} from "react-icons/fa6";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import ColorThief from "colorthief";
import { useNavigate, useParams } from "react-router-dom";

// Counter animation component
const CounterAnimation = ({
  target,
  duration = 2000,
  decimals = 0,
  start = true,
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime = null;
    const startValue = 0;
    const endValue = target;

    const animate = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easeOut;

      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [target, duration, start]);

  return <span>{count.toFixed(decimals)}</span>;
};

// Helper function to convert HSL to RGB
const hslToRgb = (h, s, l) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;

  let rgb;
  const hSector = h * 6;
  if (hSector < 1) rgb = [c, x, 0];
  else if (hSector < 2) rgb = [x, c, 0];
  else if (hSector < 3) rgb = [0, c, x];
  else if (hSector < 4) rgb = [0, x, c];
  else if (hSector < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];

  return rgb.map((v) => Math.round((v + m) * 255));
};

// Helper function to get relative luminance
const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((v) => {
    v = v / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const Recap = () => {
  const { t } = useTranslation();
  const tg = useTelegram();
  const navigate = useNavigate();
  const { token } = useParams();
  const isShareMode = !!token;

  const [currentSlide, setCurrentSlide] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(18);
  const [recapData, setRecapData] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [isSharing, setIsSharing] = useState(false);

  const [animatedBarHeights, setAnimatedBarHeights] = useState([0, 0, 0, 0, 0]);

  const [characterBgColor, setCharacterBgColor] = useState("#1a202c");
  const [characterTextColor, setCharacterTextColor] = useState("#fff");
  const [characterLoading, setCharacterLoading] = useState(true);
  const characterImgRef = useRef(null);

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: false,
    slides: { perView: 1 },
    mode: "snap",
    rubberband: false,
    slideChanged: (slider) => setCurrentSlide(slider.track.details.rel),
  });

  const filterAchievementsForCurrentYear = (currentStreak, achievementDays) => {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const daysPassedThisYear =
      Math.floor((today - startOfYear) / (1000 * 60 * 60 * 24)) + 1;

    return achievementDays.filter((days) => {
      const achievedDaysAgo = currentStreak - days;
      return achievedDaysAgo >= 0 && achievedDaysAgo <= daysPassedThisYear - 1;
    });
  };

  const achievementDays = useMemo(() => {
    if (!recapData) return [];
    return recapData.achievements.list.map((achievement) =>
      parseInt(achievement.replace("day", ""))
    );
  }, [recapData]);

  const {
    moodDistributionData,
    maxMoodCount,
    thisYearAchievements,
    thisYearAchievementStrings,
    moodColors,
  } = useMemo(() => {
    if (!recapData)
      return {
        moodDistributionData: [],
        maxMoodCount: 0,
        thisYearAchievements: [],
        thisYearAchievementStrings: [],
        moodColors: [],
      };

    const distributionData = recapData.basic_stats.mood_distribution.slice(1);
    const maxCount = Math.max(...distributionData);
    const colors = ["#dc2626", "#ea580c", "#eab308", "#65a30d", "#16a34a"];

    const thisYearAch = filterAchievementsForCurrentYear(
      recapData.mood_insights.current_day_streak,
      achievementDays
    );
    const thisYearAchStrings = thisYearAch.map((days) => `day${days}`);

    return {
      moodDistributionData: distributionData,
      maxMoodCount: maxCount,
      thisYearAchievements: thisYearAch,
      thisYearAchievementStrings: thisYearAchStrings,
      moodColors: colors,
    };
  }, [recapData, achievementDays]);

  // Визначаємо які слайди показувати
  const slidesToShow = useMemo(() => {
    if (!recapData) return [];

    const slides = [];

    slides.push("stats");

    // ВИПРАВЛЕННЯ: Умовна перевірка на існування ai_interaction
    const hasAIInteractionData =
      recapData.ai_interaction &&
      (recapData.ai_interaction.total_chat_messages > 0 ||
        recapData.ai_interaction.total_ai_analyses > 0 ||
        recapData.ai_interaction.total_tests_completed > 0);

    if (hasAIInteractionData) {
      slides.push("ai");
    }

    slides.push("mood_distribution");

    if (thisYearAchievementStrings.length > 0) {
      slides.push("achievements");
    }

    if (recapData.mood_insights?.trend) {
      slides.push("mood_insights");
    }

    slides.push("character");
    slides.push("conclusion");

    return slides;
  }, [recapData, thisYearAchievementStrings]);

  const getRealSlideIndex = (slideType) => {
    return slidesToShow.indexOf(slideType);
  };

  useEffect(() => {
    // if (!tg.initData) {
    //   console.error("Telegram initData відсутній!");
    //   return;
    // }
    const fetchRecapData = async () => {
      let response;

      try {
        setLoadingProgress(18);
        const progressInterval = setInterval(() => {
          setLoadingProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 1;
          });
        }, 1000 / 25);

        if (isShareMode) {
          // РЕЖИМ ПОШИРЕННЯ: Викликаємо getSharedRecap, передаючи токен
          response = await getSharedRecap(token);
        } else {
          // ЗВИЧАЙНИЙ РЕЖИМ: Викликаємо getRecap, використовуючи tg.initData
          if (!tg.initData) {
            console.error("Telegram initData відсутній!");
            clearInterval(progressInterval);
            setIsLoading(false);
            // Можливо, тут варто показати повідомлення про помилку
            return;
          }
          response = await getRecap();
        }

        clearInterval(progressInterval);
        setLoadingProgress(100);

        if (response.success) {
          setRecapData(response.data);
          setTimeout(() => setIsLoading(false), 1000);
        } else {
          console.error("Failed to load recap:", response.message);
          // TODO: Обробка помилки для share mode (наприклад, недійсний токен)
        }
      } catch (error) {
        console.error("Помилка завантаження recap:", error);
      }
    };

    fetchRecapData();
  }, [tg.initData, token, isShareMode]);

  useEffect(() => {
    const moodDistSlideIndex = getRealSlideIndex("mood_distribution");
    if (currentSlide === moodDistSlideIndex && moodDistSlideIndex !== -1) {
      if (maxMoodCount > 0) {
        const finalHeights = moodDistributionData.map(
          (count) => (count / maxMoodCount) * 200
        );
        setAnimatedBarHeights(finalHeights);
      }
    } else {
      setAnimatedBarHeights([0, 0, 0, 0, 0]);
    }
  }, [currentSlide, moodDistributionData, maxMoodCount, slidesToShow]);

  const extractCharacterColor = () => {
    const img = characterImgRef.current;
    if (!img) return;

    if (img.complete) {
      processColorExtraction(img);
    } else {
      img.onload = () => processColorExtraction(img);
    }
  };

  const processColorExtraction = (img) => {
    try {
      const colorThief = new ColorThief();
      const dominant = colorThief.getColor(img);

      const [r, g, b] = dominant.map((v) => v / 255);
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const delta = max - min;

      let h = 0,
        s = 0;
      const l = (max + min) / 2;

      if (delta !== 0) {
        s = delta / (1 - Math.abs(2 * l - 1));
        switch (max) {
          case r:
            h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
            break;
          case g:
            h = ((b - r) / delta + 2) / 6;
            break;
          case b:
            h = ((r - g) / delta + 4) / 6;
            break;
          default:
            h = 0;
            break;
        }
      }

      const newS = Math.min(0.7, Math.max(0.55, s * 1.6));
      const newL = Math.max(0.3, Math.min(0.65, l * 0.95));

      const saturatedRgb = hslToRgb(h, newS, newL);
      setCharacterBgColor(
        `rgb(${saturatedRgb[0]}, ${saturatedRgb[1]}, ${saturatedRgb[2]})`
      );

      const bgLuminance = getLuminance(...saturatedRgb);
      setCharacterTextColor(bgLuminance < 0.45 ? "#ffffff" : "#000000");
    } catch (err) {
      console.error("Error extracting character color:", err);
      setCharacterBgColor("#1e40af");
      setCharacterTextColor("#ffffff");
    } finally {
      setCharacterLoading(false);
    }
  };

  const handleCharacterImageLoad = () => extractCharacterColor();

  const handleShare = async () => {
  if (!tg.initData || isSharing || isShareMode) return;

  setIsSharing(true);
  try {
    const response = await postShareRecap(tg.initData);
    
    if (response.success && response.data?.share_url) {
      const fullShareUrl = response.data.share_url;
      setShareUrl(fullShareUrl);

      const shareText = `${t("recap.shareDescription")}\n\n${fullShareUrl}`;

      // Try Telegram's native share method
      if (tg.openTelegramLink) {
        const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(fullShareUrl)}&text=${encodeURIComponent(t("recap.shareDescription"))}`;
        tg.openTelegramLink(telegramShareUrl);
      }

      // Copy to clipboard (silently)
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(shareText);
        } else {
          // Fallback
          const textArea = document.createElement('textarea');
          textArea.value = shareText;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
      } catch (clipboardError) {
        console.error("Помилка копіювання:", clipboardError);
      }
    } else {
      tg.showAlert?.(t("recap.linkFailed"));
    }
  } catch (error) {
    console.error("Помилка при створенні посилання:", error);
    tg.showAlert?.(t("recap.linkFailed"));
  } finally {
    setIsSharing(false);
  }
};

  if (isLoading || !recapData) {
    return (
      <div className="relative w-full h-full flex flex-col">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-cyan-400 to-blue-400 z-50 flex flex-col items-center justify-center p-8">
          <h1 className="text-4xl font-bold text-white mb-12 animate-pulse">
            {t("recap.preparingRecap")}
          </h1>

          <div className="relative w-full max-w-md">
            <div className="h-12 bg-white/30 rounded-full overflow-hidden backdrop-blur-sm border-4 border-white/50">
              <div
                className="h-full bg-gradient-to-r from-lime-500 to-emerald-500 transition-all duration-300 ease-out relative rounded-full"
                style={{ width: `${loadingProgress}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
              </div>
            </div>

            <div
              className="absolute top-1/2 -translate-y-1/2"
              style={{
                left: `calc(${loadingProgress}% - 52px)`,
              }}
            >
              <div className="relative">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 48 48"
                  style={{
                    filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                  }}
                >
                  <circle cx="16" cy="18" r="6" fill="#fff" />
                  <circle cx="32" cy="18" r="6" fill="#fff" />
                  <circle cx="17" cy="19" r="3" fill="#000" />
                  <circle cx="33" cy="19" r="3" fill="#000" />
                  <circle cx="18" cy="17" r="1.5" fill="#fff" />
                  <circle cx="34" cy="17" r="1.5" fill="#fff" />
                  <path
                    d="M 16 28 Q 24 32 32 28"
                    stroke="#558B2F"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <ellipse
                    cx="10"
                    cy="26"
                    rx="3"
                    ry="2"
                    fill="#FFB74D"
                    opacity="0.6"
                  />
                  <ellipse
                    cx="38"
                    cy="26"
                    rx="3"
                    ry="2"
                    fill="#FFB74D"
                    opacity="0.6"
                  />
                </svg>
              </div>
            </div>
          </div>

          <p className="text-2xl font-bold text-white mt-8">
            {Math.round(loadingProgress)}%
          </p>
        </div>
      </div>
    );
  }

  const getSlides = (currentSlide) => {
    const allSlides = {
      stats: {
        component: (
          <div className="relative w-full h-screen flex flex-col p-4 pt-12 pb-4 bg-gradient-to-t from-white via-white to-cyan-600">
            <div className="relative">
              <p className="text-center font-bold text-xl">RECAP</p>
              <FaChartArea className="absolute top-8 left-2 text-cyan-100 size-32 -rotate-12" />
              <FaChartArea className="absolute bottom-8 right-4 text-cyan-50 size-28 rotate-6" />
              <img
                src="/recap/frog-2.webp"
                className="w-[90%] mx-auto relative"
                alt="Frog character 2"
              />
            </div>
            <div className="space-y-6 flex-1 flex flex-col justify-center mb-12">
              <p className="text-cyan-900 font-bold text-2xl">
                {t("recap.stats")}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-gray-950">
                  <p className="font-bold text-cyan-600 text-5xl">
                    <CounterAnimation
                      target={recapData.basic_stats.average_mood}
                      decimals={1}
                      start={getRealSlideIndex("stats") === currentSlide}
                    />
                    /5
                  </p>
                  <p className="font-semibold text-2xl">{t("recap.mood")}</p>
                </div>
                <div className="text-gray-950">
                  <p className="font-bold text-cyan-600 text-5xl">
                    <CounterAnimation
                      target={recapData.basic_stats.total_mood_entries}
                      start={getRealSlideIndex("stats") === currentSlide}
                    />
                  </p>
                  <p className="font-semibold text-2xl">{t("recap.entries")}</p>
                  <span className="text-xs">{t("recap.entriesMood")}</span>
                </div>
                <div className="text-gray-950">
                  <p className="font-bold text-cyan-600 text-5xl">
                    <CounterAnimation
                      target={recapData.basic_stats.total_notes_written}
                      start={getRealSlideIndex("stats") === currentSlide}
                    />
                  </p>
                  <p className="font-semibold text-2xl">{t("recap.notes")}</p>
                </div>
                <div className="text-gray-950">
                  <p className="font-bold text-cyan-600 text-5xl">
                    <CounterAnimation
                      target={recapData.basic_stats.total_follow_ups_completed}
                      start={getRealSlideIndex("stats") === currentSlide}
                    />
                  </p>
                  <p className="font-semibold text-2xl">{t("recap.surveys")}</p>
                </div>
                {recapData.basic_stats.additional_moods_count > 0 && (
                  <div className="text-gray-950 col-span-2">
                    <p className="font-bold text-cyan-600 text-3xl">
                      <CounterAnimation
                        target={recapData.basic_stats.additional_moods_count}
                        start={getRealSlideIndex("stats") === currentSlide}
                      />
                    </p>
                    <p className="font-semibold text-xl">
                      {t("recap.additionalMoods")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
      },
      ai: {
        component: (
          <div className="relative w-full h-screen flex flex-col p-4 pt-12 pb-4 bg-gradient-to-t from-white via-white to-emerald-600">
            <div className="relative">
              <p className="text-center font-bold text-xl">RECAP</p>
              <FaBrain className="absolute top-8 left-2 text-emerald-100 size-32 -rotate-12 z-0" />
              <FaBrain className="absolute bottom-8 right-4 text-emerald-50 size-28 rotate-6 z-0" />
              <img
                src="/recap/frog-1.webp"
                className="w-[90%] mx-auto relative"
                alt="Frog character 1"
              />
            </div>
            <div className="space-y-6 flex-1 flex flex-col justify-center mb-12">
              <p className="text-emerald-900 font-bold text-2xl">
                {t("recap.aiInteractions")}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-gray-950">
                  <p className="font-bold text-emerald-600 text-5xl">
                    <CounterAnimation
                      target={
                        recapData.ai_interaction?.total_chat_messages || 0
                      }
                      start={getRealSlideIndex("ai") === currentSlide}
                    />
                  </p>
                  <p className="font-semibold text-2xl">{t("recap.chat")}</p>
                  <span className="text-xs">{t("recap.messages")}</span>
                </div>
                <div className="text-gray-950">
                  <p className="font-bold text-emerald-600 text-5xl">
                    <CounterAnimation
                      target={recapData.ai_interaction?.total_ai_analyses || 0}
                      start={getRealSlideIndex("ai") === currentSlide}
                    />
                  </p>
                  <p className="font-semibold text-2xl">
                    {t("recap.analyses")}
                  </p>
                </div>
                <p className="text-emerald-900 font-bold text-2xl col-span-2">
                  {t("recap.tests")}
                </p>
                <div className="text-gray-950 col-span-2">
                  <p className="font-bold text-emerald-600 text-5xl">
                    <CounterAnimation
                      target={
                        recapData.ai_interaction?.total_tests_completed || 0
                      }
                      start={getRealSlideIndex("ai") === currentSlide}
                    />
                  </p>
                  <p className="font-semibold text-2xl">
                    {t("recap.completed")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ),
      },
      mood_distribution: {
        component: (
          <div className="relative w-full h-screen flex flex-col p-4 pt-12 pb-4 bg-gradient-to-t from-white via-white to-yellow-600">
            <div className="relative">
              <p className="text-center font-bold text-xl">RECAP</p>
              <FaChartArea className="absolute top-8 left-2 text-yellow-100 size-32 -rotate-12" />
              <FaChartArea className="absolute bottom-8 right-4 text-yellow-50 size-28 rotate-6" />
              <img
                src="/recap/frog-3.webp"
                className="w-[90%] mx-auto relative"
                alt="Frog character 3"
              />
            </div>
            <div className="space-y-6 flex-1 flex flex-col justify-center mb-12">
              <p className="text-yellow-900 font-bold text-2xl">
                {t("recap.moodDistribution")}
              </p>
              <div className="flex items-end justify-center gap-x-4 mb-8 h-48">
                {moodDistributionData?.map((count, index) => {
                  return (
                    <div key={index} className="flex flex-col items-center ">
                      <div className="text-xs font-bold text-gray-950 mb-1">
                        <CounterAnimation
                          target={count}
                          start={
                            getRealSlideIndex("mood_distribution") ===
                            currentSlide
                          }
                        />
                      </div>
                      <div
                        className="w-12 rounded-t max-h-32"
                        style={{
                          height: `${animatedBarHeights[index]}px`,
                          backgroundColor: moodColors[index],
                          minHeight: count > 0 ? "5px" : "0px",
                          transition: "height 3000ms ease-out",
                        }}
                      />
                      <img
                        src={`/${index + 1}.webp`}
                        className="w-2/3"
                        alt={`Mood ${index + 1}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="text-center text-gray-950">
                <p className="font-bold text-yellow-600 text-3xl">
                  {new Date(
                    recapData.year,
                    recapData.basic_stats.most_active_month.month - 1
                  ).toLocaleString("uk", { month: "long" })}
                </p>
                <p className="font-semibold text-xl">
                  {t("recap.mostActiveMonth")}
                </p>
                <span className="text-sm">
                  <CounterAnimation
                    target={recapData.basic_stats.most_active_month.count}
                    start={
                      getRealSlideIndex("mood_distribution") === currentSlide
                    }
                  />{" "}
                  {t("recap.records")}
                </span>
              </div>
            </div>
          </div>
        ),
      },
      achievements: {
        component: (
          <div className="relative w-full h-screen flex flex-col p-4 pt-12 pb-4 bg-gradient-to-t from-white via-white to-purple-600">
            <div className="relative">
              <p className="text-center font-bold text-xl">RECAP</p>
              <FaTrophy className="absolute top-8 left-2 text-purple-100 size-32 -rotate-12" />
              <FaTrophy className="absolute bottom-8 right-4 text-purple-50 size-28 rotate-6" />
              <img
                src="/recap/frog-4.webp"
                className="w-[90%] mx-auto relative"
                alt="Frog character 4"
              />
            </div>
            <div className="space-y-6 flex-1 flex flex-col justify-center mb-12">
              <p className="text-purple-900 font-bold text-2xl">
                {t("recap.achievements")}
              </p>
              <div className="text-center">
                <p className="font-bold text-purple-600 text-5xl mb-4">
                  <CounterAnimation
                    target={thisYearAchievements.length}
                    start={getRealSlideIndex("achievements") === currentSlide}
                  />
                </p>
                <p className="text-purple-900 font-semibold text-2xl mb-4">
                  {t("recap.thisYear")}
                </p>

                {/* === ОБГОРТКА ДЛЯ МАСКИ === */}
                <div className="relative max-w-lg mx-auto">
                  {/* МАСКА (Верхня біла тінь) */}
                  <div
                    className="absolute top-0 left-0 right-0 h-8 z-10 
                                    bg-gradient-to-b from-white to-transparent pointer-events-none"
                  />

                  {/* ОСНОВНИЙ КОНТЕЙНЕР ПРОКРУТКИ */}
                  <div className="grid grid-cols-3 gap-x-4 max-w-lg mx-auto max-[380px]:h-32 h-64 overflow-y-auto">
                    {thisYearAchievementStrings.map((achievement, idx) => (
                      <div key={idx} className="text-center mb-4">
                        <img
                          src={`/achievement_${achievement}.webp`}
                          alt={`${achievement} achievement`}
                          // Змінено клас для зображення, щоб воно краще виглядало в сітці
                          className="w-full mx-auto object-contain"
                        />
                        <p className="text-xs font-semibold text-purple-600">
                          {achievement.replace("day", t("recap.day"))}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* МАСКА (Нижня біла тінь) */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-8 z-10 
                                    bg-gradient-to-t from-white to-transparent pointer-events-none"
                  />
                </div>
                {/* ========================== */}
              </div>
            </div>
          </div>
        ),
      },
      mood_insights: {
        component: (
          <div className="relative w-full h-screen flex flex-col p-4 pt-12 pb-4 bg-gradient-to-t from-white via-white to-red-600">
            <div className="relative">
              <p className="text-center font-bold text-xl">RECAP</p>
              <FaFire className="absolute top-8 left-2 text-red-100 size-32 -rotate-12" />
              <FaFire className="absolute bottom-8 right-4 text-red-50 size-28 rotate-6" />
              <img
                src="/recap/frog-5.webp"
                className="w-[90%] mx-auto relative"
                alt="Frog character 5"
              />
            </div>
            <div className="space-y-6 flex-1 flex flex-col justify-center mb-12">
              <p className="text-red-900 font-bold text-2xl">
                {t("recap.moodInsights")}
              </p>
              <div className="grid grid-cols-1 gap-4">
                <div className="text-gray-950 text-center">
                  <p className="font-bold text-red-600 text-5xl">
                    <CounterAnimation
                      target={recapData.mood_insights.current_day_streak}
                      start={
                        getRealSlideIndex("mood_insights") === currentSlide
                      }
                    />
                  </p>
                  <p className="font-semibold text-2xl">
                    {t("recap.dayStreak")}
                  </p>
                </div>
                <div className="text-gray-950 text-center">
                  <p className="font-bold text-red-600 text-5xl">
                    {recapData.mood_insights.trend?.improvement_percentage >= 0
                      ? "+"
                      : ""}
                    <CounterAnimation
                      target={
                        recapData.mood_insights.trend?.improvement_percentage
                      }
                      decimals={1}
                      start={
                        getRealSlideIndex("mood_insights") === currentSlide
                      }
                    />
                    %
                  </p>
                  <p className="font-semibold text-2xl">
                    {recapData.mood_insights.trend?.improvement_percentage >= 0
                      ? t("recap.improvement")
                      : t("recap.worsening")}
                  </p>
                </div>
                <div className="text-gray-950 text-center">
                  <p className="text-lg">
                    {recapData.mood_insights.trend?.improvement_percentage >= 0
                      ? t("recap.moodImproved")
                      : t("recap.moodWorsened")}{" "}
                    <span className="font-bold text-red-600">
                      <CounterAnimation
                        target={recapData.mood_insights.trend?.start_average}
                        decimals={1}
                        start={
                          getRealSlideIndex("mood_insights") === currentSlide
                        }
                      />
                    </span>{" "}
                    {t("recap.to")}{" "}
                    <span className="font-bold text-red-600">
                      <CounterAnimation
                        target={recapData.mood_insights.trend?.end_average}
                        decimals={1}
                        start={
                          getRealSlideIndex("mood_insights") === currentSlide
                        }
                      />
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        ),
      },
      character: {
        component: (
          <div
            className="flex flex-col h-screen items-center justify-center flex-1 transition-colors duration-300"
            style={{ backgroundColor: characterBgColor }}
          >
            <div className="flex-1 flex flex-col justify-center relative">
              <div className="relative mx-auto w-[90%]">
                {characterLoading && (
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <div className="animate-spin rounded-full h-24 w-24 border-t-4 border-b-4 border-white"></div>
                  </div>
                )}
                <img
                  ref={characterImgRef}
                  src={`/recap/${recapData.frog_character.type}.webp`}
                  crossOrigin="anonymous"
                  onLoad={handleCharacterImageLoad}
                  className={`relative z-10 animate-shake transition-opacity duration-300 ${
                    characterLoading ? "opacity-0" : "opacity-100"
                  }`}
                  alt="Character"
                />
                {!characterLoading && (
                  <div className="absolute inset-0 animate-pulse rounded-full filter blur-3xl bg-white opacity-40"></div>
                )}
              </div>
              {!characterLoading && (
                <p
                  className="text-2xl mt-4 font-bold mx-8 text-center"
                  style={{ color: characterTextColor }}
                >
                  {t("recap.you")} {recapData.frog_character.name}
                </p>
              )}
              {!characterLoading && (
                <p
                  className="text-lg mt-2 font-semibold mx-8 text-center"
                  style={{ color: characterTextColor }}
                >
                  {recapData.frog_character.description}
                </p>
              )}
              {!characterLoading && (
                <p
                  className="text-base mt-1 font-medium mx-8 text-center"
                  style={{ color: characterTextColor }}
                >
                  {t("recap.rarity")}:{" "}
                  {Math.round(recapData.frog_character.rarity_percentage)}%
                </p>
              )}
            </div>
          </div>
        ),
      },
      conclusion: {
        component: (
          <div className="relative w-full h-screen flex flex-col p-4 pt-12 pb-4 bg-gradient-to-t from-red-200 via-red-50 to-red-600">
            <div className="relative">
              <p className="text-center font-bold text-xl">RECAP</p>
              <FaGifts className="absolute top-8 left-2 text-red-100 size-32 -rotate-12" />
              <FaGift className="absolute bottom-8 right-4 text-red-50 size-28 rotate-6" />
              <img
                src="/recap/frog-6.webp"
                className="w-[90%] mx-auto relative"
                alt="Frog character 6"
              />
            </div>
            <div className="space-y-6 flex-1 flex flex-col justify-center mb-24 text-center">
              <div className="text-gray-950 space-y-4">
                {isShareMode ? (
                  <>
                    <p className="text-lg font-bold text-red-900">
                      {t("recap.callToAction")}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-red-900 font-bold">
                      {t("recap.thanks")}
                    </p>
                    <p className="text-xs font-semibold">
                      {t("recap.continueJournaling")}
                    </p>
                    <p className="text-xs font-semibold">
                      {t("recap.newYear")}
                    </p>
                  </>
                )}
              </div>

              {!isShareMode && (
                <button
                  onClick={handleShare}
                  disabled={isSharing}
                  className="mx-auto px-8 py-2 bg-gradient-to-r from-emerald-400 to-cyan-500 text-white font-bold rounded-full shadow-xl hover:from-emerald-500 hover:to-cyan-600 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSharing ? t("recap.creatingLink") : t("recap.share")}
                </button>
              )}
            </div>
          </div>
        ),
      },
    };

    // Фільтруємо та повертаємо тільки потрібні компоненти
    return slidesToShow.map((slideKey) => ({
      component: allSlides[slideKey].component,
      key: slideKey,
    }));
  };

  const slides = getSlides(currentSlide);

  const handleNext = () => {
    if (currentSlide === slides.length - 1) {
      if (isShareMode) {
        // РЕЖИМ ПОШИРЕННЯ: Закриваємо Mini App, повертаючи користувача до Telegram.
        // Це вирішує проблему блокування навігації всередині TMA.
        tg.close();
      } else {
        // ЗВИЧАЙНИЙ РЕЖИМ: Переходимо на головну сторінку
        navigate("/");
      }
    } else {
      instanceRef.current?.next();
    }
  };

  if (!isLoading) {
    return (
      <div className="relative w-full h-full flex flex-col">
        <div ref={sliderRef} className="keen-slider w-full h-full">
          {slides.map((slide, index) => (
            <div key={slide.key} className="keen-slider__slide h-full">
              {slide.component}
            </div>
          ))}
        </div>

        <div className="absolute top-10 left-0 right-0 flex justify-center space-x-2 z-10">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => instanceRef.current?.moveToIdx(idx)}
              className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                currentSlide === idx ? "bg-white shadow-lg" : "bg-white/50"
              }`}
              aria-label={`Slide ${idx + 1}`}
            />
          ))}
        </div>

        <div className="absolute bottom-4 left-4 right-4 flex justify-between gap-4">
          <button
            onClick={() => instanceRef.current?.prev()}
            disabled={currentSlide === 0}
            className="py-2 px-4 bg-white/80 rounded-lg disabled:opacity-50 text-gray-900 shadow-md"
            aria-label="Previous slide"
          >
            <FaChevronLeft />
          </button>
          <button
            onClick={handleNext}
            className="flex-1 py-2 text-2xl font-bold rounded-lg bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move text-white shadow-lg"
          >
            {currentSlide === slides.length - 1
              ? (isShareMode ? t("recap.close") : t("recap.toMain"))
              : t("guide.next")}
          </button>
        </div>
      </div>
    );
  }
};

export default Recap;